import { Component, OnInit, inject } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ConfirmationService, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { ButtonGroupModule } from "primeng/buttongroup";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogModule } from "primeng/dialog";
import { SelectModule } from "primeng/select";
import { InputTextModule } from "primeng/inputtext";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import { TextareaModule } from "primeng/textarea";
import { ToastModule } from "primeng/toast";
import { NewsCategory, NewsItem } from "../../_models/news-item";
import { NewsService } from "../../_services/news.service";

interface NewsItemForm {
  id?: string;
  title: string;
  dateString: string;
  category: NewsCategory;
  summary: string;
  content: string;
  externalLink: string;
  externalLinkTitle: string;
}

const EMPTY_FORM: NewsItemForm = {
  title: "",
  dateString: new Date().toISOString().split("T")[0],
  category: "General",
  summary: "",
  content: "",
  externalLink: "",
  externalLinkTitle: ""
};

@Component({
  selector: "app-admin-news",
  templateUrl: "./admin-news.component.html",
  styleUrls: ["./admin-news.component.css"],
  standalone: true,
  providers: [ConfirmationService, MessageService],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ButtonGroupModule,
    ConfirmDialogModule,
    DialogModule,
    SelectModule,
    InputTextModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    DatePipe
  ]
})
export class AdminNewsComponent implements OnInit {
  newsService = inject(NewsService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);

  items: NewsItem[] = [];
  dialogVisible = false;
  isEditMode = false;
  form: NewsItemForm = { ...EMPTY_FORM };

  readonly categories: NewsCategory[] = [
    "Software",
    "Data",
    "Events",
    "Publication",
    "General"
  ];
  readonly categoryOptions = this.categories.map(c => ({ label: c, value: c }));

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.newsService.getAll().subscribe(items => (this.items = items));
  }

  openAddDialog(): void {
    this.isEditMode = false;
    this.form = { ...EMPTY_FORM };
    this.dialogVisible = true;
  }

  openEditDialog(item: NewsItem): void {
    this.isEditMode = true;
    this.form = {
      id: item.id,
      title: item.title,
      dateString: item.date.toISOString().split("T")[0],
      category: item.category,
      summary: item.summary,
      content: item.content ?? "",
      externalLink: item.externalLink ?? "",
      externalLinkTitle: item.externalLinkTitle ?? ""
    };
    this.dialogVisible = true;
  }

  save(): void {
    if (!this.form.title.trim() || !this.form.summary.trim()) {
      this.messageService.add({
        severity: "warn",
        summary: "Validation",
        detail: "Title and Summary are required."
      });
      return;
    }
    const data: Omit<NewsItem, "id"> = {
      title: this.form.title.trim(),
      date: new Date(this.form.dateString),
      category: this.form.category,
      summary: this.form.summary.trim(),
      content: this.form.content.trim() || undefined,
      externalLink: this.form.externalLink.trim() || undefined,
      externalLinkTitle: this.form.externalLinkTitle.trim() || undefined
    };

    if (this.isEditMode) {
      this.newsService.update({ ...data, id: this.form.id }).subscribe(() => {
        this.messageService.add({
          severity: "success",
          summary: "Saved",
          detail: "News item updated."
        });
        this.dialogVisible = false;
        this.reload();
      });
    } else {
      this.newsService.create(data).subscribe(() => {
        this.messageService.add({
          severity: "success",
          summary: "Created",
          detail: "News item added."
        });
        this.dialogVisible = false;
        this.reload();
      });
    }
  }

  confirmDelete(item: NewsItem): void {
    this.confirmationService.confirm({
      header: "Delete News Item",
      message: `Delete "${item.title}"? This cannot be undone.`,
      acceptButtonStyleClass: "p-button-danger",
      accept: () => {
        this.newsService.delete(item.id).subscribe(() => {
          this.messageService.add({
            severity: "success",
            summary: "Deleted",
            detail: "News item removed."
          });
          this.reload();
        });
      }
    });
  }
}
